import React, { useState } from "react";
import { Alert, Modal, Radio, Typography, notification } from "antd";
import { useTranslation } from "react-i18next";

import api from "../../../../api";
import { KontaxTransactionFilterOptions } from "../../../../types";
import { getServerErrorNotification } from "../../../../utils/error";
import {
  buildExportFileName,
  ExportFileNameUser,
  getFileNameFromContentDisposition,
} from "../../../../utils/exportFileName";
import { downloadFile } from "../../CommandHubView/utils";

/**
 * The PDF is rendered by the html2pdf lambda behind an API Gateway with a 12 s
 * timeout, so a wide filter can exceed the request budget rather than just being
 * slow. Warn well before that ceiling; tune once Task 10 measures the real cliff.
 */
export const PDF_ROW_WARNING_THRESHOLD = 1000;

type Props = {
  visible: boolean;
  onClose: () => void;
  userId: number;
  userLanguage?: string;
  /** Customer, used to build the file name when the backend sends none. */
  user?: ExportFileNameUser;
  filters: KontaxTransactionFilterOptions;
  rowCount: number;
};

const DownloadTransactionsModal = ({
  visible,
  onClose,
  userId,
  userLanguage,
  user,
  filters,
  rowCount,
}: Props) => {
  /*
   * Two independent languages meet in this modal:
   * - the Backoffice UI language (`t`), which is the operator's own setting and
   *   decides how this modal reads;
   * - the DOCUMENT language (`language` below), which is the customer's and
   *   decides what the exported file is written in.
   * Don't collapse them: an English-speaking operator still routinely exports a
   * German document for a German customer.
   */
  const { t } = useTranslation("DownloadTransactions");
  const [filetype, setFiletype] = useState<"csv" | "pdf">("csv");
  // The common case is sending the file on to the customer, so start from the
  // customer's own profile language rather than a fixed default.
  const [language, setLanguage] = useState<"de" | "en">(
    userLanguage === "en" ? "en" : "de"
  );
  const [isDownloading, setIsDownloading] = useState(false);

  const showsPdfWarning =
    filetype === "pdf" && rowCount > PDF_ROW_WARNING_THRESHOLD;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await api.kontax.exportFilteredTransactions(
        userId,
        filters,
        filetype,
        language
      );
      const contentType = response.headers["content-type"];
      const blob = new Blob([response.data], {
        type: typeof contentType === "string" ? contentType : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      // The backend already builds the customer-named filename; prefer it and
      // only fall back to the same name built here if the header is missing.
      const contentDisposition = response.headers["content-disposition"];
      const fileName =
        getFileNameFromContentDisposition(
          typeof contentDisposition === "string"
            ? contentDisposition
            : undefined
        ) ||
        buildExportFileName({
          user: user ?? { email: filters.email },
          exportType: "Transaktionen",
          period:
            filters.dateFrom && filters.dateTo
              ? {
                  kind: "dateRange",
                  from: filters.dateFrom,
                  to: filters.dateTo,
                }
              : undefined,
          extension: filetype,
        });
      downloadFile(url, fileName);
      onClose();
    } catch (error) {
      const { message, description } = getServerErrorNotification(
        error,
        t("notifError")
      );
      notification.error({ message, ...(description ? { description } : {}) });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={t("modalTitle")}
      onCancel={onClose}
      onOk={handleDownload}
      okText={t("btnDownload")}
      cancelText={t("btnCancel")}
      confirmLoading={isDownloading}
      data-test="downloadTransactionsModal"
    >
      <Typography.Paragraph>
        {t("fileType")}
        <br />
        <Radio.Group
          value={filetype}
          onChange={(event) => setFiletype(event.target.value)}
        >
          <Radio value="csv">CSV</Radio>
          <Radio value="pdf">PDF</Radio>
        </Radio.Group>
      </Typography.Paragraph>

      <Typography.Paragraph>
        {t("language")}
        <br />
        <Radio.Group
          value={language}
          onChange={(event) => setLanguage(event.target.value)}
        >
          <Radio value="de">{t("languageDe")}</Radio>
          <Radio value="en">{t("languageEn")}</Radio>
        </Radio.Group>
        <br />
        <Typography.Text type="secondary">{t("languageHint")}</Typography.Text>
      </Typography.Paragraph>

      {showsPdfWarning && (
        <Alert
          role="alert"
          type="warning"
          showIcon
          message={t("pdfRowWarning", { count: rowCount })}
        />
      )}
    </Modal>
  );
};

export default DownloadTransactionsModal;
